/* eslint-disable better-mutation/no-mutation */

import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { type ITEM_STATES, VIEW_ITEM_STATES } from './constants';
import TRANSACTION_TYPES from 'tradera/constants/transaction-types';
import { placeBidApi } from './api';
import { logger } from 'packages/logger';
import * as analytics from '../../../apps/tradera/components/vip/helpers/analytics';
import * as itemActions from '../../items/actions';
import { assertNoUndefinedProperties } from 'tradera/utils/assert';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode,
} from 'tradera/state/language/selectors';
import {
    selectBidInfo,
    selectItemId,
    selectItemDetails,
    selectBreadcrumbs,
    selectViewItem,
} from './selectors';
import { GtmService as gtm } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { getItemState } from './item-state-helper';
import { BID_VALIDATION_RESULTS } from 'tradera/apps/tradera/components/vip/api/vip-api-constants';
import { selectExistsInWishList } from 'tradera/state/items/selectors';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { areLanguageCodesIso2Equal } from 'tradera/localization/languagecode';
import type {
    ItemBidInfoViewModel,
    ItemCategoryViewModel,
    ItemDetailsViewModel,
    ItemPageModel,
    ItemPurchaseViewModel,
    ItemTranslationsViewModel,
    PaymentApiDto,
} from 'tradera/state/services/types/webapi-browse-generated';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import type ITEM_TYPES from 'tradera/constants/item-types';
import type {
    ItemCardViewModel,
    SearchResultItem2ApiDto,
} from 'tradera/state/services/types/webapi-discover-generated';
import { DEFAULT_LANGUAGE } from 'tradera/lang/constants.mjs';
import { wishlist } from 'tradera/state/items/reducer';
import { SOURCE_SCREEN } from 'tradera/services/google-tagmanager/constants';

type PlaceBidResponse = {
    info?: string | null;
    isSuccessful: boolean | null;
    validationResult: {
        code: BID_VALIDATION_RESULTS;
    } | null;
    error: unknown | null;
};

const initialPlaceBidResponse: PlaceBidResponse = {
    info: null,
    isSuccessful: null,
    validationResult: null,
    error: null,
};

export enum translatedDescriptionStates {
    DEFAULT = 'DEFAULT',
    LOADING = 'LOADING',
    SERVER_ERROR = 'SERVER_ERROR',
    TRANSLATION_ON_DEMAND_DISABLED = 'TRANSLATION_ON_DEMAND_DISABLED',
}

type ItemTranslations = ItemTranslationsViewModel & {
    providerName: string;
};

export type ItemDetails = Omit<ItemDetailsViewModel, 'images'> & {
    images?: {
        thumbnail?: string | null;
        multiThumbnail?: string | null;
        medium?: string | null;
        normal?: string | null;
        smallFit?: string | null;
        mediumFit?: string | null;
        largeFit?: string | null;
        video?: string | null;
    }[];
};

type Breadcrumbs = {
    id: number;
    url?: string | null;
    name?: string | null;
    categoryId: number;
};

type BidInfo = Omit<ItemBidInfoViewModel, 'bidCount'> & {
    bidId?: number | null;
    bidCount: number | null;
    hasLeadingBid?: boolean;
};

type BidToasterData = {
    leadingBidMemberAlias?: string;
    placeBidValidationCode?: string;
    leadingBidAmount?: number;
    nextValidBidAmount?: number;
    myMaxBidAmount?: number;
};

export type SellerItems = {
    items: ItemCardViewModel[];
    itemsMatched: number;
};

export enum BuyerProtectionAvailabilityReasons {
    AUTHENTICATED_ITEM = 'AuthenticatedItem',
    RESTRICTED_CATEGORY = 'RestrictedCategory',
    EXCLUDED_BY_CAMPAIGN = 'ExcludedByCampaign',
    OPTIONAL_FOR_SWEDISH_COMPANY = 'OptionalForSwedishCompany',
    PRICE_LIST = 'PriceList',
}

export type ViewItemState = {
    bidInfo: BidInfo | null;
    bidToasterData: BidToasterData | null;
    breadCrumbs: Breadcrumbs[];
    categoryItems: ItemCardViewModel[];
    combinedShippingItems:
        | SearchResultItem2ApiDto[]
        | ItemCardViewModel[]
        | null;
    currentDescriptionLanguageCodeIso2: string | null;
    isBidding: boolean;
    itemDetails: ItemDetails;
    itemState: ITEM_STATES | null;
    myMaxBidAmount: number | null;
    placeBidResponse: PlaceBidResponse;
    purchaseInfo: ItemPurchaseViewModel | null;
    sellerItems: SellerItems | null;
    serverSecondsLeft: number | null;
    shopItems: ItemCardViewModel[];
    translatedDescription: ItemTranslations | null;
    translatedDescriptionState: translatedDescriptionStates;
    viewItemState: VIEW_ITEM_STATES | null;
    isWishlistPending: boolean;
    payment?: PaymentApiDto | null;
};

export const initialState: ViewItemState = {
    bidInfo: null,
    bidToasterData: null,
    breadCrumbs: [],
    isBidding: false,
    itemDetails: {} as ItemDetails,
    categoryItems: [],
    shopItems: [],
    sellerItems: null,
    combinedShippingItems: null,
    itemState: null,
    viewItemState: null,
    myMaxBidAmount: null,
    placeBidResponse: initialPlaceBidResponse,
    purchaseInfo: null,
    serverSecondsLeft: null,
    translatedDescription: null,
    translatedDescriptionState: translatedDescriptionStates.DEFAULT,
    currentDescriptionLanguageCodeIso2: null,
    isWishlistPending: false,
    payment: null,
};

const viewItemSlice = createSlice({
    name: 'viewItem',
    initialState,
    reducers: {
        resetPlaceBidResponse: (state) => {
            state.placeBidResponse = initialPlaceBidResponse;
        },
        resetInitialState: () => {
            return initialState;
        },
        setShowBiddingToaster: (
            state,
            action: PayloadAction<BidToasterData>,
        ) => {
            state.bidToasterData = action.payload;
        },
        setIsRestricted: (state, { payload }) => {
            state.itemDetails.isRestricted = payload;
        },
        setIsInternationalWacko: (state, action: PayloadAction<boolean>) => {
            if (!state.itemDetails.wackoInfo) {
                throw new Error('wackoInfo is null');
            }

            state.itemDetails.wackoInfo.isInternationalWacko = action.payload;
        },
        setItemDetails: (
            state,
            action: PayloadAction<ItemDetailsViewModel>,
        ) => {
            state.itemDetails = mapItemDetails(action.payload);
        },
        setPayment: (state, action: PayloadAction<PaymentApiDto | null>) => {
            state.payment = action.payload;
        },
        setPurchaseInfo: (
            state,
            action: PayloadAction<ItemPurchaseViewModel | null>,
        ) => {
            state.purchaseInfo = action.payload;
        },
        setBreadCrumbs: (
            state,
            action: PayloadAction<ItemCategoryViewModel[]>,
        ) => {
            state.breadCrumbs = action.payload.map(
                ({ id, href, text }, index) => ({
                    id: index,
                    url: href,
                    name: text,
                    categoryId: id,
                }),
            );
        },
        setIsBidding: (state, action: PayloadAction<boolean>) => {
            state.isBidding = action.payload;
        },
        setPlaceBidResponse: (
            state,
            action: PayloadAction<PlaceBidResponse>,
        ) => {
            const { payload } = action;
            state.placeBidResponse.isSuccessful = payload.isSuccessful;
            state.placeBidResponse.validationResult = payload.validationResult;
            state.placeBidResponse.error = payload.error;
        },
        setBidInfo: (
            state,
            action: PayloadAction<{
                bidInfo: BidInfo;
                itemId: number;
                memberId: number;
            }>,
        ) => {
            const { bidInfo, itemId, memberId } = action.payload;
            // Ensure bid is for current item.
            if (state.itemDetails?.itemId !== itemId) {
                logger(new Error(`setBidInfo was not for current item`), {
                    contexts: {
                        items: {
                            currentItemId: state.itemDetails?.itemId,
                            bidInfoItemId: itemId,
                        },
                    },
                });
                return;
            }

            // Ensure incoming data is for latest bid. It is possible to get stale data in
            // the response from place bid due to eventual consistency. When that happens
            // we should have received the latest bid before via web socket. For items where
            // web socket updates are not enabled (too long time left of auction) the effect
            // is considered acceptable. Just make sure to not overwrite newer data here.
            const isFirstBidInfo = !state.bidInfo;
            const isBinPurchase =
                bidInfo.transactionType === TRANSACTION_TYPES.BIN;
            const isLatestBid =
                isFirstBidInfo ||
                isBinPurchase ||
                (state.bidInfo?.bidId ?? 0) <= (bidInfo.bidId ?? 0);
            if (!isLatestBid) return;

            const bidStatus = getLeadingBidStatus(
                bidInfo,
                memberId,
                state.bidInfo,
            );

            const existingBidCount = state.bidInfo?.bidCount || 0;
            const incomingBidCount = bidInfo.bidCount || 0;
            const newBidInfo = {
                bidId: bidInfo.bidId || 0,
                leadingBidAmount: bidInfo.leadingBidAmount || 0,
                leadingBidderMemberId: bidInfo.leadingBidderMemberId || null,
                leadingBidMemberAlias: bidInfo.leadingBidMemberAlias || null,
                nextValidBidAmount: bidInfo.nextValidBidAmount,
                transactionType: state.bidInfo?.transactionType
                    ? state.bidInfo.transactionType
                    : bidInfo.transactionType || null,

                // Never update with a lower bidCount. Can happen if place-bid-response comes before WebSocket event.
                bidCount:
                    incomingBidCount > existingBidCount
                        ? incomingBidCount
                        : existingBidCount,

                // Never reset if reservePriceReached was true. Can happen if place-bid-response comes before WebSocket event.
                reservePriceReached:
                    state.bidInfo?.reservePriceReached ||
                    bidInfo.reservePriceReached ||
                    false,
            };

            assertNoUndefinedProperties(newBidInfo);

            state.bidInfo = { ...newBidInfo, ...bidStatus };
        },
        setMyMaxBidAmount: (state, action: PayloadAction<number>) => {
            state.myMaxBidAmount = action.payload;
        },
        setItemState: (state, action: PayloadAction<ITEM_STATES>) => {
            state.itemState = action.payload;
        },
        setViewItemState: (state, action: PayloadAction<VIEW_ITEM_STATES>) => {
            state.viewItemState = action.payload;
        },
        setServerSecondsLeft: (state, action: PayloadAction<number>) => {
            state.serverSecondsLeft = action.payload;
        },
        setTranslatedDescription: (
            state,
            action: PayloadAction<ItemTranslations>,
        ) => {
            state.translatedDescription = action.payload;
            state.translatedDescriptionState =
                translatedDescriptionStates.DEFAULT;
        },
        setCurrentDescriptionLanguageCodeIso2: (
            state,
            action: PayloadAction<string>,
        ) => {
            state.currentDescriptionLanguageCodeIso2 = action.payload;
        },
        setTranslationOnDemandDisabled: (state) => {
            state.translatedDescription = null;
            state.translatedDescriptionState =
                translatedDescriptionStates.TRANSLATION_ON_DEMAND_DISABLED;
        },
        resetTranslatedDescription: (state) => {
            state.translatedDescription = null;
            state.translatedDescriptionState =
                translatedDescriptionStates.DEFAULT;
        },
        beginLoadingTranslatedDescription: (state) => {
            state.translatedDescription = null;
            state.translatedDescriptionState =
                translatedDescriptionStates.LOADING;
        },
        failedLoadingTranslatedDescription: (state) => {
            state.translatedDescriptionState =
                translatedDescriptionStates.SERVER_ERROR;
        },
        setSellerItems: (state, action: PayloadAction<SellerItems | null>) => {
            state.sellerItems = action.payload;
        },
        setCategoryItems: (
            state,
            action: PayloadAction<ItemCardViewModel[]>,
        ) => {
            state.categoryItems = action.payload;
        },
        setShopItems: (state, action: PayloadAction<ItemCardViewModel[]>) => {
            state.shopItems = action.payload;
        },
        setCombinedShippingItems: (
            state,
            action: PayloadAction<
                SearchResultItem2ApiDto[] | ItemCardViewModel[]
            >,
        ) => {
            state.combinedShippingItems = action.payload;
        },
        setIsWishlistPending: (state, action: PayloadAction<boolean>) => {
            state.isWishlistPending = action.payload;
        },
        setPaymentAmountForBid: (
            state,
            action: PayloadAction<number | null>,
        ) => {
            if (state.itemDetails.paymentCalculations) {
                state.itemDetails.paymentCalculations.paymentAmountForBid =
                    action.payload;
            }
        },
    },
});

export default viewItemSlice.reducer;

export const mapItemDetails = (
    itemDetails: ItemDetailsViewModel,
): ItemDetails => ({
    ...itemDetails,
    descriptionLanguageCodeIso2:
        itemDetails.descriptionLanguageCodeIso2?.toLowerCase(),
    images: itemDetails.images?.map((img) => ({
        thumbnail: img.thumbnail,
        multiThumbnail: img.thumbnail,
        medium: img.small,
        normal: img.normal,
        smallFit: img.smallFit,
        mediumFit: img.mediumFit,
        largeFit: img.largeFit,
        video: img.video || null,
    })),
});

export const handleItemModel =
    (itemModel: ItemPageModel, memberId: number) =>
    (dispatch: AppDispatch, getState: GetState) => {
        if (!itemModel.itemDetails) throw new Error('itemDetails is missing');

        const previousItemId = selectItemId(getState());
        dispatch(viewItemSlice.actions.setItemDetails(itemModel.itemDetails));
        dispatch(setTranslation(itemModel));

        dispatch(viewItemSlice.actions.setPayment(itemModel.payment ?? null));

        dispatch(
            viewItemSlice.actions.setPurchaseInfo(
                itemModel.purchaseInfo ?? null,
            ),
        );

        dispatch(
            wishlist({
                itemId: itemModel.itemDetails.itemId,
                exists: !!itemModel.itemDetails.inWishList,
            }),
        );
        dispatch(
            viewItemSlice.actions.setBreadCrumbs(itemModel.breadCrumbs ?? []),
        );

        if (!itemModel.bidInfo) throw new Error('bidInfo is missing');
        dispatch(
            viewItemSlice.actions.setBidInfo({
                bidInfo: itemModel.bidInfo,
                itemId: itemModel.itemDetails.itemId,
                memberId,
            }),
        );

        if (itemModel.bidInfo.myMaxBidAmount) {
            dispatch(
                viewItemSlice.actions.setMyMaxBidAmount(
                    itemModel.bidInfo.myMaxBidAmount,
                ),
            );
        }

        const isSafeToShowWinnerStatus =
            itemModel.itemDetails.hasEnded &&
            !itemModel.hasBidsWinnerToBeConfirmed;

        const viewItemState = isSafeToShowWinnerStatus
            ? VIEW_ITEM_STATES.ENDED
            : itemModel.itemDetails.hasStarted
              ? VIEW_ITEM_STATES.ACTIVE
              : VIEW_ITEM_STATES.NOT_STARTED;

        dispatch(viewItemSlice.actions.setViewItemState(viewItemState));

        const { itemDetails, bidInfo } = selectViewItem(getState());
        if (!bidInfo) throw new Error('bidInfo is missing');

        dispatch(updateItemState(itemDetails, bidInfo, viewItemState));

        dispatch(
            viewItemSlice.actions.setServerSecondsLeft(
                itemModel.serverSecondsLeft,
            ),
        );

        const isNewPage = previousItemId != selectItemId(getState());
        if (isNewPage) {
            dispatch(handleNewPage);
        }
    };

const setTranslation =
    (itemModel: ItemPageModel) =>
    (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();

        const shippingRegion = selectShippingRegionCountryCodeIso2(state);
        const preferredLanguageCodeIso2 = selectPreferredLanguageCode(state);

        const disabledForEnglishLangSwedes =
            isSwedenCountryCodeOrUndefined(shippingRegion) &&
            areLanguageCodesIso2Equal(preferredLanguageCodeIso2, 'en');
        const automaticTranslationPreferred =
            selectAutomaticTranslationPreferred(state);

        const descriptionLanguageCodeIso2 =
            itemModel.itemDetails?.descriptionLanguageCodeIso2 ||
            DEFAULT_LANGUAGE;

        const shouldTranslateDescriptionOnLoad =
            automaticTranslationPreferred &&
            preferredLanguageCodeIso2 !== descriptionLanguageCodeIso2 &&
            !disabledForEnglishLangSwedes;

        dispatch(
            viewItemSlice.actions.setCurrentDescriptionLanguageCodeIso2(
                shouldTranslateDescriptionOnLoad
                    ? preferredLanguageCodeIso2
                    : descriptionLanguageCodeIso2,
            ),
        );
        if (itemModel.translations) {
            dispatch(
                viewItemSlice.actions.setTranslatedDescription({
                    ...itemModel.translations,
                    providerName: 'Google Translate',
                }),
            );
        } else {
            dispatch(viewItemSlice.actions.resetTranslatedDescription());
        }
    };

export const handleNewPage = (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const currentItemDetails = selectItemDetails(state);
    const currentBidInfo = selectBidInfo(state);
    const breadCrumbs = selectBreadcrumbs(state);

    const swedishCategoryPath = breadCrumbs.map((b) => b.name).join(' > ');

    const getAuctionState = () => {
        const hasWon =
            currentItemDetails.hasEnded && currentBidInfo?.hasLeadingBid;
        const isLeading =
            !currentItemDetails.hasEnded && currentBidInfo?.hasLeadingBid;
        if (hasWon) return 'won';
        if (isLeading) return 'leading';
        return currentItemDetails.hasEnded ? 'ended' : 'normal';
    };

    const contentGroup = `${currentItemDetails.itemType}-${getAuctionState()}`;
    gtm.trackGtmEvent('viewItem', {
        page: {
            itemState: contentGroup,
            'content-group-2': swedishCategoryPath,
            'content-group-3': contentGroup,
        },
        itemType: currentItemDetails.itemType,
        itemId: currentItemDetails.itemId,
        itemFixedPrice: currentItemDetails.fixedPrice || 0,
        itemCategory: swedishCategoryPath,
        itemStatus: currentItemDetails.hasEnded ? 'inactive' : 'active',
        itemEndDate: dayjs(currentItemDetails.endDate).format('YYYYMMDD'),
        timestamp: Date.now(),
    });
};

const updateItemState =
    (
        itemDetails: ItemDetails,
        bidInfo: BidInfo,
        viewItemState: VIEW_ITEM_STATES,
    ) =>
    (dispatch: AppDispatch) => {
        if (!itemDetails.itemType) throw new Error('itemType is missing');
        if (!bidInfo.nextValidBidAmount)
            throw new Error('nextValidBidAmount is missing');

        const itemType = itemDetails.itemType as ITEM_TYPES;
        const transactionType = bidInfo.transactionType as TRANSACTION_TYPES;

        const itemState = getItemState(
            viewItemState === VIEW_ITEM_STATES.ENDED,
            viewItemState !== VIEW_ITEM_STATES.NOT_STARTED,
            itemType,
            bidInfo.bidCount ?? 0,
            bidInfo.reservePriceReached,
            bidInfo.nextValidBidAmount,
            transactionType,
            bidInfo.leadingBidderMemberId ?? undefined,
            itemDetails.isCancelled,
        );
        dispatch(viewItemSlice.actions.setItemState(itemState));
    };

const getLeadingBidStatus = (
    bidInfo: BidInfo,
    memberId: number,
    previousBidInfo: BidInfo | null = null,
) => {
    const hadLeadingBid = !!previousBidInfo?.hasLeadingBid;
    const hasLeadingBid =
        !!memberId && memberId === bidInfo.leadingBidderMemberId;
    const hadLostLeadingBid =
        !!previousBidInfo?.hasLostLeadingBid || bidInfo.hasLostLeadingBid;
    const hasLostLeadingBid =
        (hadLostLeadingBid || hadLeadingBid) && !hasLeadingBid;
    return {
        hasLostLeadingBid,
        hasLeadingBid,
    };
};

export const toggleWishList =
    (itemId: number, forceAdd = false) =>
    (dispatch: AppDispatch, getState: GetState) => {
        if (getState().views.viewItem.isWishlistPending) {
            return;
        }
        dispatch(viewItemSlice.actions.setIsWishlistPending(true));
        const inWishListOldValue =
            getState().items.wishListItemIds.includes(itemId);
        const inWishListNewValue = !inWishListOldValue || forceAdd;

        const itemDetails = getState().views.viewItem.itemDetails;

        const wishListCountOldValue = itemDetails.wishListCount ?? 0;
        let wishListCountNewValue = wishListCountOldValue;
        if (Number.isInteger(wishListCountNewValue)) {
            if (inWishListOldValue && !inWishListNewValue) {
                wishListCountNewValue--;
            } else if (!inWishListOldValue && inWishListNewValue) {
                wishListCountNewValue++;
            }
        }
        dispatch(
            viewItemSlice.actions.setItemDetails({
                ...itemDetails,
                inWishList: inWishListNewValue,
                wishListCount: wishListCountNewValue,
            }),
        );

        return dispatch(
            itemActions.wishlist(itemId, {
                value: inWishListNewValue,
                shouldNotTrackEventBecauseNeedsToBeSameAsLegacyVip: true,
                showWishlistModals: !forceAdd,
                sectionGaName: 'VIP',
                gaSourceScreen: SOURCE_SCREEN.VIP,
                gaWishlistType: 'VIP',
            }),
        )
            .then((inWishListNewValue) => {
                if (inWishListNewValue !== undefined) {
                    analytics.trackWishlist(itemId, inWishListNewValue);
                } else {
                    //Restore previous value;
                    const itemDetails = getState().views.viewItem.itemDetails;
                    dispatch(
                        viewItemSlice.actions.setItemDetails({
                            ...itemDetails,
                            inWishList: inWishListOldValue,
                            wishListCount: wishListCountOldValue,
                        }),
                    );
                }
                return inWishListNewValue;
            })
            .finally(() => {
                dispatch(viewItemSlice.actions.setIsWishlistPending(false));
            });
    };

export const placeBid =
    (
        itemId: number,
        memberId: number,
        amount: number,
        onLoggedOut: () => void,
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        dispatch(viewItemSlice.actions.setIsBidding(true));

        const existsInWishList = selectExistsInWishList(itemId)(getState());
        const preferredLanguageCode = selectPreferredLanguageCode(getState());
        const { gaTrackingSessionId } = getState().environment;

        const itemBeforeBid = selectItemDetails(getState());

        try {
            const result = await placeBidApi(
                itemId,
                amount,
                onLoggedOut,
                preferredLanguageCode,
                gaTrackingSessionId ?? '',
            );

            if (!result) {
                dispatch(
                    viewItemSlice.actions.setPlaceBidResponse({
                        isSuccessful: false,
                        validationResult: {
                            code: BID_VALIDATION_RESULTS.OFFLINE,
                        },
                        error: null,
                    }),
                );
                dispatch(viewItemSlice.actions.setIsBidding(false));
                dispatch(
                    viewItemSlice.actions.setShowBiddingToaster({
                        placeBidValidationCode: BID_VALIDATION_RESULTS.OFFLINE,
                    }),
                );
                return;
            }
            const {
                bidInfo,
                isSuccessful,
                item: updatedItem,
                validationResult,
                myMaxBidAmount,
                serverSecondsLeft,
            } = result;

            if (isSuccessful) {
                dispatch(
                    viewItemSlice.actions.setBidInfo({
                        bidInfo,
                        itemId,
                        memberId,
                    }),
                );
                dispatch(
                    viewItemSlice.actions.setMyMaxBidAmount(myMaxBidAmount),
                );
            } else if (bidInfo.leadingBidAmount) {
                dispatch(
                    viewItemSlice.actions.setBidInfo({
                        bidInfo,
                        itemId,
                        memberId,
                    }),
                );
            }
            dispatch(
                viewItemSlice.actions.setShowBiddingToaster({
                    leadingBidMemberAlias: bidInfo.leadingBidMemberAlias,
                    placeBidValidationCode: validationResult.code,
                    leadingBidAmount: bidInfo.leadingBidAmount,
                    nextValidBidAmount: bidInfo.nextValidBidAmount,
                    myMaxBidAmount: result.myMaxBidAmount,
                }),
            );

            const analyticsData = extractAnalyticsData(itemBeforeBid, result);
            analytics.trackBidPlaced(
                itemId,
                validationResult.code,
                analyticsData?.isMaxBid,
                analyticsData?.potentialBidChange,
                analyticsData?.bidId,
                analyticsData?.transactionTotal,
                analyticsData?.leadingBidAmount,
            );

            const currentItemDetails = getState().views.viewItem.itemDetails;
            dispatch(
                viewItemSlice.actions.setItemDetails({
                    ...currentItemDetails,
                    ...updatedItem,
                }),
            );

            dispatch(
                viewItemSlice.actions.setPlaceBidResponse({
                    isSuccessful,
                    validationResult: {
                        code: validationResult.code as BID_VALIDATION_RESULTS,
                    },
                    error: null,
                }),
            );

            dispatch(
                viewItemSlice.actions.setServerSecondsLeft(serverSecondsLeft),
            );
        } catch (error) {
            logger(error);
            dispatch(
                viewItemSlice.actions.setPlaceBidResponse({
                    isSuccessful: false,
                    validationResult: null,
                    error,
                }),
            );
        }
        dispatch(viewItemSlice.actions.setIsBidding(false));

        // Add item to wish list if it doesn't already exist in list
        if (!existsInWishList) {
            dispatch(toggleWishList(itemId, true));
        }
    };

const extractAnalyticsData = (
    itemBeforeBid: ItemDetails,
    placeBidResponse: Awaited<ReturnType<typeof placeBidApi>>,
) => {
    if (!placeBidResponse) return null;

    const { bidInfo } = placeBidResponse;

    const transactionTotal =
        bidInfo.leadingBidAmount - (itemBeforeBid.leadingBid || 0);
    const potentialBidChange =
        placeBidResponse.myMaxBidAmount - (itemBeforeBid.leadingBid || 0);
    const isMaxBid =
        placeBidResponse.myMaxBidAmount === placeBidResponse.item.leadingBid;

    return {
        isMaxBid,
        potentialBidChange,
        bidId: bidInfo.bidId,
        transactionTotal,
        leadingBidAmount: bidInfo.leadingBidAmount,
    };
};

export const {
    beginLoadingTranslatedDescription,
    failedLoadingTranslatedDescription,
    resetPlaceBidResponse,
    resetTranslatedDescription,
    setBidInfo,
    setIsRestricted,
    setIsInternationalWacko,
    setViewItemState,
    setTranslatedDescription,
    setTranslationOnDemandDisabled,
    setSellerItems,
    setCategoryItems,
    setShopItems,
    setCombinedShippingItems,
    setCurrentDescriptionLanguageCodeIso2,
    setPaymentAmountForBid,
} = viewItemSlice.actions;
